import { MobileContainer } from '@/components/workflow/mobile-container';
import { MobileHeader } from '@/components/workflow/mobile-header';
import { Badge, Button, Flex, Image, Space, Text, TextInput, Title } from '@mantine/core';
import { IconCamera, IconPackage, IconPlus, IconReceipt, IconScan } from '@tabler/icons-react';
import { CustomerValue, ManualCreateScannedBale, ManualLoadCreateFormData } from '../context';
import { useBaleInboundContext } from '../hook';
import styles from '../styles.module.css';

/**
 * Section showing scanned bales with material type cards
 */
const ScannedBalesSection: React.FC<{
	materials: ManualCreateScannedBale[];
	totalCount: number;
	hasScans: boolean;
	onRemove: () => void;
	onScan: () => void;
}> = ({ materials, totalCount, hasScans, onRemove, onScan }) => (
	<Flex direction="column" w="100%" gap="sm">
		{/* Section Header */}
		<Flex align="center" justify="space-between" px="md" w="100%">
			<Title order={2}>Scanned Bales</Title>
			<Badge bg={hasScans ? 'green' : 'red'} size="md" fw={500}>
				{totalCount} Bales
			</Badge>
		</Flex>

		{/* Bale Cards or Empty State */}
		<Flex className={styles.horizontalScrollContainer}>
			{hasScans ? (
				materials.map((bale) => (
					<Flex key={bale.value} className={styles.baleCard}>
						<Flex
							direction="column"
							align="center"
							justify="space-between"
							p="md"
							h="100%"
							w="100%"
						>
							{/* Material Info */}
							<Flex direction="column" align="center" gap="xs" flex={1} justify="center">
								<Text
									size="40px"
									lh={1}
									style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
								>
									{bale.emoji}
								</Text>
								<Text
									size="xs"
									fw={600}
									ta="center"
									lh={1.1}
									tt="uppercase"
									style={{ letterSpacing: '0.5px' }}
								>
									{bale.name}
								</Text>
							</Flex>

							{/* Count Badge */}
							<Badge
								size="sm"
								fw={600}
								style={{
									fontSize: '11px',
									minWidth: '60px',
									textAlign: 'center',
								}}
							>
								{bale.count}
							</Badge>
						</Flex>
					</Flex>
				))
			) : (
				// Empty State
				<Flex
					style={{ border: '1px dashed var(--mantine-color-gray-6)', borderRadius: '6px' }}
					w="100%"
					p="md"
					direction="column"
					c="gray"
					justify="center"
					align="center"
					mx="md"
					gap="sm"
				>
					<IconPackage size={48} />
					<Text size="md">No bales scanned yet</Text>
				</Flex>
			)}
		</Flex>

		{/* Action Buttons */}
		<Flex px="md" gap="md">
			<Button
				onClick={onRemove}
				fullWidth
				disabled={!hasScans}
				variant="light"
				bg="grey"
				color="white"
				leftSection={<IconPlus size={16} />}
			>
				Remove
			</Button>
			<Button onClick={onScan} fullWidth variant="filled" leftSection={<IconScan size={16} />}>
				Scan
			</Button>
		</Flex>
	</Flex>
);

/**
 * Form section for entering load details
 */
const LoadDetailsSection: React.FC<{
	formData: ManualLoadCreateFormData;
	onInput: (
		key: keyof ManualLoadCreateFormData,
		value: string | number | string[] | CustomerValue | null
	) => void;
}> = ({ formData, onInput }) => {
	const isComplete = formData.weight && formData.truckRegistration;

	return (
		<Flex direction="column" w="100%" gap="sm">
			{/* Section Header */}
			<Flex px="md" align="center" justify="space-between" w="100%">
				<Title order={2}>Load Details</Title>
				<Badge bg={isComplete ? 'green' : 'red'} size="md" fw={500}>
					{isComplete ? 'Okay' : 'Required'}
				</Badge>
			</Flex>

			{/* Form Fields */}
			<Flex direction="column" px="md" gap={0}>
				{/* Weight and Registration Row */}
				<Flex gap="md">
					<TextInput
						size="md"
						type="number"
						label="Load Weight"
						value={formData.weight ? (formData.weight / 1000).toString() : ''}
						onChange={(e) => {
							const value = e.target.value;
							const numValue = value === '' ? null : parseFloat(value) * 1000;
							onInput('weight', numValue);
						}}
						required
						w="100%"
						rightSection={
							<Text size="sm" c="var(--mantine-color-grey-5)" pr="xl">
								Tonne
							</Text>
						}
					/>
					<TextInput
						required
						value={formData.truckRegistration ?? ''}
						onChange={(e) => onInput('truckRegistration', e.target.value)}
						size="md"
						label="Truck Registration"
						placeholder="ABC-123"
						w="100%"
					/>
				</Flex>
			</Flex>
		</Flex>
	);
};

/**
 * Section for docket information and photo
 */
const DocketSection: React.FC<{
	formData: ManualLoadCreateFormData;
	onInput: (
		key: keyof ManualLoadCreateFormData,
		value: string | number | string[] | CustomerValue | null
	) => void;
	onAddPhoto: () => void;
}> = ({ formData, onInput, onAddPhoto }) => {
	const isComplete = formData.docketPhoto && formData.docketId;

	return (
		<Flex direction="column" px="md" w="100%" gap="lg">
			{/* Section Header */}
			<Flex align="center" justify="space-between" w="100%">
				<Title order={2}>Docket</Title>
				<Badge bg={isComplete ? 'green' : 'red'} size="md" fw={500}>
					{isComplete ? 'Okay' : 'Required'}
				</Badge>
			</Flex>

			{/* Docket ID Input */}
			<TextInput
				required
				size="md"
				value={formData.docketId ?? ''}
				onChange={(e) => onInput('docketId', e.target.value)}
				label="Docket ID"
				placeholder="Enter number on docket"
				w="100%"
			/>

			{/* Photo Display or Empty State */}
			{formData.docketPhoto ? (
				<Flex className={styles.docketPhotoContainer}>
					<Image src={formData.docketPhoto} alt="Docket Photo" />
				</Flex>
			) : (
				<Flex
					w="100%"
					direction="column"
					style={{ border: '1px dashed var(--mantine-color-gray-6)', borderRadius: '6px' }}
					c="gray"
					p="md"
					justify="center"
					align="center"
					gap="sm"
				>
					<IconReceipt size={48} />
					<Text size="md">Add docket photo</Text>
				</Flex>
			)}

			{/* Photo Action Button */}
			<Flex gap="md">
				{formData.docketPhoto ? (
					<Button
						onClick={() => onInput('docketPhoto', null)}
						fullWidth
						variant="filled"
						bg="gray"
						leftSection={<IconPlus size={16} />}
					>
						Discard
					</Button>
				) : (
					<Button
						onClick={onAddPhoto}
						fullWidth
						variant="filled"
						leftSection={<IconCamera size={16} />}
					>
						Add Photo
					</Button>
				)}
			</Flex>
		</Flex>
	);
};

export const ManualInboundLoadCreatePage = () => {
	const { setPage, formData, handleManualCreateInput, scannedBales } = useBaleInboundContext();

	const scannedMaterials = scannedBales.filter((bale) => bale.count > 0);

	/** Total count of scanned bales across all material types */
	const totalScannedBales = scannedMaterials.reduce((sum, bale) => sum + bale.count, 0);

	/** Whether any bales have been scanned */
	const hasScans = scannedMaterials.length > 0;

	/**
	 * Navigate to the bale removal page
	 */
	const handleRemoveBales = () => {
		setPage('MANUAL_CREATE:REMOVE_BALE');
	};

	/**
	 * Navigate to the bale scanning page
	 */
	const handleScanBales = () => {
		setPage('MANUAL_CREATE:ADD_BALE');
	};

	/**
	 * Navigate to the docket photo capture page
	 */
	const handleAddDocketPhoto = () => {
		setPage('DOCKET_PHOTO');
	};

	return (
		<MobileContainer
			header={<MobileHeader title="Enter Load Details" onBack={() => setPage('INITIAL_SCAN')} />}
		>
			<ScannedBalesSection
				materials={scannedMaterials}
				totalCount={totalScannedBales}
				hasScans={hasScans}
				onRemove={handleRemoveBales}
				onScan={handleScanBales}
			/>

			{/* Load Details Section */}
			<LoadDetailsSection formData={formData} onInput={handleManualCreateInput} />

			{/* Docket Section */}
			<DocketSection
				formData={formData}
				onInput={handleManualCreateInput}
				onAddPhoto={handleAddDocketPhoto}
			/>
			<Space h={100} />
		</MobileContainer>
	);
};
